import { useMutation, useQuery } from "@tanstack/react-query"
import { Card, Divider } from "antd"
import type React from "react"
import { useReducer } from "react"
import RoleTable from "./components/RoleTable"
import RoleTableActions from "./components/RoleTableActions"
import RoleModal from "./components/RoleModal"
import PermissionModal from "./components/PermissionModal"
import RoleAccountsModal from "./components/RoleAccountsModal"
import { roleService, type RoleFormParams } from "@/services/role/roleApi"

interface PageState {
  modalVisible: boolean
  modalTitle: string
  currentRecord: any | null
  permissionVisible: boolean
  accountsVisible: boolean
  selectedRoleId: number | string | null
  selectedRoleName: string
}

export const RolePage: React.FC = () => {
  const [state, dispatch] = useReducer(
    (prev: PageState, action: Partial<PageState>) => ({
      ...prev,
      ...action,
    }),
    {
      modalVisible: false,
      modalTitle: "Thêm vai trò",
      currentRecord: null,
      permissionVisible: false,
      accountsVisible: false,
      selectedRoleId: null,
      selectedRoleName: "",
    }
  )

  const {
    data: result,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["roles_list"],
    queryFn: () => roleService.list(),
  })

  const saveRoleMutation = useMutation({
    mutationFn: ({ id, params }: { id?: number | string; params: RoleFormParams }) => {
      if (id) {
        return roleService.update(id, params)
      }
      return roleService.create(params)
    },
    onSuccess: () => {
      dispatch({
        modalVisible: false,
        currentRecord: null,
      })
      refetch()
    },
  })

  const handleAdd = () => {
    dispatch({
      modalVisible: true,
      modalTitle: "Thêm vai trò mới",
      currentRecord: null,
    })
  }

  const handleEdit = (record: any) => {
    dispatch({
      modalVisible: true,
      modalTitle: "Cập nhật vai trò",
      currentRecord: record,
    })
  }

  const handleStatusChange = (record: any, checked: boolean) => {
    saveRoleMutation.mutate({
      id: record.id,
      params: {
        name: record.name,
        description: record.description,
        status: checked ? 1 : 0,
      },
    })
  }

  const handleAssignPermissions = (record: any) => {
    dispatch({
      permissionVisible: true,
      selectedRoleId: record.id,
      selectedRoleName: record.name,
    })
  }

  const handleViewAccounts = (record: any) => {
    dispatch({
      accountsVisible: true,
      selectedRoleId: record.id,
      selectedRoleName: record.name,
    })
  }

  const handleModalOk = (values: RoleFormParams) => {
    saveRoleMutation.mutate({
      id: state.currentRecord?.id,
      params: values,
    })
  }

  const handleModalCancel = () => {
    dispatch({
      modalVisible: false,
      currentRecord: null,
    })
  }

  const tableLoading = isFetching || saveRoleMutation.isPending

  return (
    <div className="flex h-full flex-col gap-2">
      <Card
        className="flex min-h-0 grow flex-col"
        classNames={{ body: "flex grow" }}
        title={
          <div className="flex items-center">
            <h2>Danh sách vai trò</h2>
            <Divider orientation="vertical" />
            <RoleTableActions onAdd={handleAdd} onRefresh={refetch} loading={tableLoading} />
          </div>
        }
      >
        <RoleTable
          data={result?.data || []}
          loading={tableLoading}
          onEdit={handleEdit}
          onAssignPermissions={handleAssignPermissions}
          onViewAccounts={handleViewAccounts}
          onStatusChange={handleStatusChange}
        />
      </Card>

      <RoleModal
        open={state.modalVisible}
        title={state.modalTitle}
        loading={saveRoleMutation.isPending}
        initialValues={state.currentRecord}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
      />

      <PermissionModal
        open={state.permissionVisible}
        roleId={state.selectedRoleId}
        roleName={state.selectedRoleName}
        onCancel={() => dispatch({ permissionVisible: false, selectedRoleId: null, selectedRoleName: "" })}
      />

      <RoleAccountsModal
        open={state.accountsVisible}
        roleId={state.selectedRoleId}
        roleName={state.selectedRoleName}
        onCancel={() => dispatch({ accountsVisible: false, selectedRoleId: null, selectedRoleName: "" })}
      />
    </div>
  )
}

export default RolePage
